import React from "react";
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { TemplateType } from "../../../../../stores/flowsTemplatesStore";
import { useCreateTemplateModal, TEMPLATE_TYPES } from "./useCreateTemplateModal";
import BaseModal from "../../../../../components/Modal/BaseModal";
import { CreateTemplateModalProps } from "./types";

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  open,
  onClose,
}) => {
  const {
    name,
    templateType,
    selectedFile,
    loading,
    dragOver,
    fileInputRef,
    setName,
    setTemplateType,
    setSelectedFile,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleSubmit,
    handleClose,
    formatFileSize,
    isSubmitDisabled,
  } = useCreateTemplateModal({ onClose });

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title="Import Template"
      maxWidth="sm"
      fullWidth
      cancelText="Cancel"
      submitText="Create Template"
      onSubmit={handleSubmit}
      submitDisabled={isSubmitDisabled}
      submitLoading={loading}
      dataTestId="create-template-modal"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* File Upload Zone */}
        <input
          id="template-file-input"
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          accept="application/pdf,.pdf"
        />
        <Box
          component="label"
          htmlFor={selectedFile ? undefined : "template-file-input"}
          sx={{
            border: 2,
            borderStyle: "dashed",
            borderColor: dragOver ? "primary.main" : "divider",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: dragOver ? "primary.light" : "grey.50",
            transition: "all 0.2s ease",
            display: "block",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "grey.100",
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {selectedFile ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
              <InsertDriveFileIcon sx={{ fontSize: 40, color: "primary.main" }} />
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedFile(null);
                }}
              >
                <DeleteIcon />
              </Button>
            </Box>
          ) : (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={500}>
                Drag & drop any file here
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PDF, Word, Excel, or any format
              </Typography>
            </Box>
          )}
        </Box>

        <TextField
          fullWidth
          label="Template Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Monthly Portfolio Report"
          inputProps={{ "data-testid": "template-name-input" }}
        />

        <FormControl fullWidth>
          <InputLabel>Template Type</InputLabel>
          <Select
            value={templateType}
            label="Template Type"
            onChange={(e) => setTemplateType(e.target.value as TemplateType)}
            data-testid="template-type-select"
          >
            {TEMPLATE_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </BaseModal>
  );
};
